import { currentLocale } from "../i18n";
import type { ExportColumn } from "./xlsx";

/**
 * Printing a list as PDF, in the browser.
 *
 * Same story as the xlsx export: the old application sent the grid's XML to an
 * external PHP service (`<export_url_excel_pdf>/export/pdf/generate.php`) to be
 * rendered. The rows are already here, so nothing has to leave the browser.
 *
 * Deliberately a *table* renderer, not a document layout engine. A printable
 * speech plan — headed paper, congregation details, one week per block — is a
 * different job and belongs on the server, where the data and the wording
 * already are.
 */

/** Landscape by default: the grids are wide, some carry a dozen columns. */
const MARGIN = 28;

function formattedNow(): string {
  return new Intl.DateTimeFormat(currentLocale(), {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date());
}

/** Mirrors the xlsx rule so both exports read the same. */
function textFor(
  value: string | number | boolean | null | undefined,
  yes: string,
): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value ? yes : "";
  return String(value);
}

export async function exportToPdf<T>({
  rows,
  columns,
  fileName,
  title,
  yes,
}: {
  rows: T[];
  columns: ExportColumn<T>[];
  fileName: string;
  /** Printed above the table; the screen's own heading. */
  title: string;
  /** The localised "yes" — keeps this file free of i18n wiring. */
  yes: string;
}): Promise<void> {
  // Loaded on demand, like the xlsx writer: together they are the better part
  // of a megabyte, and most visits print nothing.
  const [{ jsPDF }, { default: autoTable }] = await Promise.all([
    import("jspdf"),
    import("jspdf-autotable"),
  ]);

  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const stamp = formattedNow();

  autoTable(doc, {
    head: [columns.map((column) => column.header)],
    body: rows.map((row) => columns.map((column) => textFor(column.value(row), yes))),
    startY: MARGIN + 24,
    margin: { top: MARGIN + 24, right: MARGIN, bottom: MARGIN + 12, left: MARGIN },
    styles: { fontSize: 7, cellPadding: 3, overflow: "linebreak" },
    headStyles: { fontStyle: "bold", fillColor: [240, 240, 240], textColor: 20 },
    // Repeated on every page, so a table running over several sheets stays
    // readable once printed.
    showHead: "everyPage",
    theme: "grid",
    didDrawPage: () => {
      const width = doc.internal.pageSize.getWidth();
      const height = doc.internal.pageSize.getHeight();

      doc.setFontSize(11);
      doc.setTextColor(20);
      doc.text(title, MARGIN, MARGIN);

      doc.setFontSize(8);
      doc.setTextColor(120);
      doc.text(stamp, width - MARGIN, MARGIN, { align: "right" });

      // getNumberOfPages counts what exists so far; on the last page that is
      // the total, which is why the "x / y" is drawn per page rather than
      // patched in afterwards.
      const page = doc.getNumberOfPages();
      doc.text(String(page), width - MARGIN, height - MARGIN + 6, { align: "right" });
    },
  });

  const date = new Date().toISOString().slice(0, 10);
  doc.save(`${fileName}-${date}.pdf`);
}
